import React from 'react';
import tw from 'twin.macro';
import tinycolor from 'tinycolor2';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { motion, MotionStyle, MotionValue } from 'framer-motion';

import { PlayerMode } from '$/components/WebinarPlayer/types';
import CardMain from '$/components/Interactions/cards/CardMain';
import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';

import isMobile from '$/utils/isMobile';
import { logger } from '$/utils/logger';

import { InteractionInfo } from '../InteractionInfo';
import { AttendeeCardForm } from './AttendeeCardForm';

interface CardProps {
  interaction: InteractionInfo;
  className?: string;
  nextInteraction?: InteractionInfo;
  snappedIdRef?: React.RefObject<string | undefined>;
  style?: MotionStyle & {
    filter?: MotionValue<string>;
    bodyOpacity?: MotionValue<number>;
    headerHeight?: MotionValue<number>;
  };
  renderingTemplatePreview?: boolean;
  cardRef?: ((instance: HTMLDivElement) => void) | React.MutableRefObject<HTMLDivElement> | null;
}

interface CardImageStyleComponent {
  floatText?: string;
}

const CardImageStyleComponent = styled.img<CardImageStyleComponent>`
  ${tw`absolute w-full h-20 rounded-lg rounded-b-none`}
  min-height: 120px;
  filter: ${(props): string | undefined => (props.floatText ? 'brightness(0.7)' : undefined)};
`;

const AttendeeCard: React.FC<CardProps> = withWebinarPlayerContext(
  [
    'playbackPositionRef',
    'mode',
    'ewebinar',
    'markInteractionActive',
    'isPlaying',
    'dimensions',
    'dimensionsRef',
    'setFocusedCardMode',
    'activeInteractionId',
    'scrollToCardOrPosition',
    'transitions',
    'isCollapsedEmbeddedPlayer',
  ],
  'AttendeeCard'
)((props) => {
  const {
    interaction,
    snappedIdRef,
    className,
    style: motionStyle,
    cardRef,
    context: {
      mode,
      scrollToCardOrPosition,
      activeInteractionId,
      setFocusedCardMode,
      markInteractionActive,
      ewebinar,
      dimensions,
      dimensionsRef,
      transitions,
      isCollapsedEmbeddedPlayer,
    },
    renderingTemplatePreview,
    nextInteraction,
  } = props;
  const isEdit = mode === PlayerMode.edit;

  const isDragging = React.useRef(false);
  const localCardRef = React.useRef<HTMLDivElement>(null);
  const CardComponent = React.useMemo(() => interaction.cardComponent(), [interaction]);
  if (!interaction.details) return null;

  const shadowColor = tinycolor(ewebinar.primaryColor).toHsl();
  shadowColor.s = 10;
  shadowColor.l = 60;

  const primaryColorGlare = tinycolor(ewebinar.primaryColor).toHsv();
  primaryColorGlare.s = 5;
  primaryColorGlare.v = 90;
  //const primaryColorGlareHex = tinycolor(primaryColorGlare).toHexString();

  const { bodyOpacity, headerHeight, rotateX, filter, visibility, ...style } = motionStyle ?? {};
  const onCardClick = React.useCallback(() => {
    if (isDragging.current) {
      return;
    }
    markInteractionActive(interaction);
    scrollToCardOrPosition(interaction);

    // On mobile we have to put the card into a special focus mode
    // when the card is taller than the height of the actionsPane
    if (isMobile && dimensionsRef.current.actionsPane.location === 'bottom') {
      const height = localCardRef.current?.clientHeight ?? 0;

      if (
        height >
        dimensionsRef.current.actionsPane.heightPx -
          dimensionsRef.current.actionsPane.marginTopPx -
          dimensionsRef.current.actionsPane.snappedCardTopPx
      ) {
        logger.debug('ANIM', 'Card click', {
          cardHeight: height,
          actionsPaneHeight:
            dimensionsRef.current.actionsPane.heightPx -
            dimensionsRef.current.actionsPane.marginTopPx -
            dimensionsRef.current.actionsPane.snappedCardTopPx,
        });

        // Go into focused card mode
        setFocusedCardMode(true);
      }
    }
  }, [
    dimensionsRef,
    interaction,
    markInteractionActive,
    scrollToCardOrPosition,
    setFocusedCardMode,
  ]);

  const onCardCoverClick = React.useCallback(() => {
    // Don't do anything to enable IFrame cards in edit mode
    if (isEdit) return;
    markInteractionActive(interaction);
    scrollToCardOrPosition(interaction);
  }, [interaction, isEdit, markInteractionActive, scrollToCardOrPosition]);

  const onMouseDown = React.useCallback((): void => {
    isDragging.current = false;
  }, []);

  const onMouseMove = React.useCallback((): void => {
    isDragging.current = true;
  }, []);

  const attendeeCardForm = React.useMemo(() => {
    return (
      <AttendeeCardForm interaction={interaction}>
        <CardMain
          interaction={interaction}
          snappedIdRef={snappedIdRef}
          bodyOpacity={bodyOpacity}
          headerHeight={headerHeight}
          nextInteraction={nextInteraction}
        >
          <CardComponent />
        </CardMain>
      </AttendeeCardForm>
    );
  }, [interaction, snappedIdRef, bodyOpacity, headerHeight, nextInteraction, CardComponent]);

  return (
    <motion.div
      id={`attendeeCard-${interaction.shortId}`}
      key={'ac-' + interaction.shortId}
      ref={cardRef ?? localCardRef}
      className={`
              ${className}
            `}
      css={[
        tw`flex flex-col w-full`,
        css`
          will-change: transform;
          transform-origin: top center;
          perspective: 500px;
          opacity: ${isEdit ? 1 : 0};
          pointer-events: none;

          position: absolute;
          top: 0;
          width: ${dimensions.actionsPane.location === 'bottom' || renderingTemplatePreview
            ? '100%;'
            : `${dimensions.actionsPane.icard.widthRem}rem;`};

          perspective: 100px;
          perspective-origin: top;
        `,
      ]}
      style={{ ...style, filter, visibility }}
      onClick={onCardClick}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
    >
      <motion.div
        ref={localCardRef}
        css={css`
          will-change: transform;
          transform-origin: top;
          opacity: ${isEdit ? 1 : 0};
        `}
        style={{ rotateX: rotateX ?? 0 }}
        variants={{
          [`card.new.${interaction.id}`]: {
            opacity: 0,
            y: '100vh',
          },
          [`card.show.${interaction.id}`]: {
            pointerEvents: 'all',
            opacity: 1,
            y: '0vh',
          },
          ['card.notification.hide']: {
            opacity: 0,
            pointerEvents: 'none',
          },
          ['card.show.all']: {
            pointerEvents: 'all',
            opacity: 1,
          },
          'card.hide.all': {
            pointerEvents: 'none',
            opacity: 0,
          },
          'actions.close': {
            opacity: 0,
            transition: { duration: 0.4, type: 'tween' },
            pointerEvents: 'none',
          },
          [`card.compress.${interaction.id}`]: {
            pointerEvents: 'all',
          },
          ...(isCollapsedEmbeddedPlayer
            ? {}
            : {
                'actions.open': {
                  opacity: 1,
                  pointerEvents: 'all',
                  transition: transitions['actions.open'],
                },
              }),
        }}
      >
        {/* Add absolute top layer to cards have have an IFrame so scrolling works. Make onClick set focus mode to card on */}
        {interaction.hasIFrame() && activeInteractionId !== interaction.id && (
          <div
            id='CardScrollCover'
            className='absolute top-0 left-0 w-full h-full z-player-card-scroll-cover'
            onClick={onCardCoverClick}
          />
        )}
        {attendeeCardForm}
      </motion.div>
    </motion.div>
  );
});

export default AttendeeCard;
